/**
 * Listing editor (§7.2).
 *
 * The listing subscription is a platform charge and is shown with its state and
 * next charge date, because a lapsed subscription silently removes the bot from
 * the marketplace. The figure the owner sets is their own asking figure, which
 * the platform never touches (§9.1) — the vocabulary here follows §7.5 exactly.
 */

import { useEffect, useState } from 'react';
import { GRANTABLE_CAPABILITIES, PERIOD_UNIT } from '@pm/shared';

import { useApi, useMutation } from '../../hooks/useApi';
import { useBotOutlet } from '../botOutlet';
import type { GrantableCapability, Listing, Page, PeriodUnit } from '../../api/types';
import { Gate } from '../../components/Gate';
import { Card, Field, Badge, Loading, LiveRegion, Banner } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { amountLabel, humanize, localDate } from '../../lib/format';
import { ASKING_LABEL, OWNER_LABEL, RENTER_LABEL } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

interface Draft {
  rental_price: number;
  currency: string;
  period_unit: PeriodUnit;
  period_count: number;
  min_periods: number;
  max_periods: number;
  granted_permissions: GrantableCapability[];
  included_generation_credits: { text: number; image: number };
  generation_overage_bearer: 'renter' | 'owner';
  terms: string;
}

const BLANK: Draft = {
  rental_price: 0,
  currency: 'USD',
  period_unit: 'week',
  period_count: 1,
  min_periods: 1,
  max_periods: 4,
  granted_permissions: ['generate', 'post', 'read_posts'],
  included_generation_credits: { text: 50, image: 5 },
  generation_overage_bearer: 'renter',
  terms: '',
};

export function ListingEditor() {
  const { bot, caps } = useBotOutlet();
  const list = useApi<Page<Listing>>(routes.bots.listings(bot.id));
  const existing = list.data?.data[0] ?? null;

  const [draft, setDraft] = useState<Draft>(BLANK);
  const save = useMutation<Record<string, unknown>, Listing>(
    existing ? routes.listings.one(existing.id) : routes.bots.listings(bot.id),
    { method: existing ? 'PATCH' : 'POST', idempotent: !existing },
  );

  useEffect(() => {
    if (!existing) return;
    setDraft({
      rental_price: existing.asking.amount,
      currency: existing.asking.currency,
      period_unit: existing.period_unit,
      period_count: existing.period_count,
      min_periods: existing.min_periods,
      max_periods: existing.max_periods,
      granted_permissions: existing.granted_permissions,
      included_generation_credits: existing.included_generation_credits,
      generation_overage_bearer: existing.generation_overage_bearer,
      terms: existing.terms,
    });
  }, [existing]);

  if (list.initialLoading) return <Loading label="Loading the listing" />;
  if (list.problem) return <ErrorView problem={list.problem} onRetry={list.reload} />;

  const sub = existing?.subscription ?? null;

  return (
    <div className="listing-editor">
      {sub ? (
        <Card title="Listing subscription">
          <p>
            <Badge tone={sub.status === 'active' ? 'ok' : sub.status === 'past_due' ? 'bad' : 'neutral'}>
              {humanize(sub.status)}
            </Badge>{' '}
            {amountLabel(sub.amount)} per {sub.interval}
          </p>
          <p className="muted">
            {sub.cancelled_at
              ? `Cancelled ${localDate(sub.cancelled_at)}; the listing ends at the period end.`
              : `Next charge ${localDate(sub.current_period_end)}.`}
          </p>
          {sub.status === 'past_due' ? (
            <Banner tone="bad" title="This subscription is past due">
              The listing is withdrawn from the marketplace until the charge succeeds. Update your
              card under Account → Billing.
            </Banner>
          ) : null}
        </Card>
      ) : null}

      {existing?.status === 'paused' ? (
        <Banner tone="warn" title="This listing is paused">
          {existing.paused_reason ?? 'It is not visible in the marketplace.'}
        </Banner>
      ) : null}

      <Gate
        caps={caps}
        capability="list_bot"
        fallback={
          <Card title="Listing">
            <p className="muted">Only the owner of this bot can list it.</p>
          </Card>
        }
      >
        {(disabled) => (
          <Card title={existing ? 'Edit the listing' : 'Create a listing'}>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if ((await save.run({ ...draft })) !== null) list.reload();
              }}
            >
              <fieldset disabled={disabled}>
                <Field
                  id="l-asking"
                  label={`${ASKING_LABEL} in minor units`}
                  hint={`You collect this directly from the ${RENTER_LABEL.toLowerCase()}. The platform never handles it.`}
                >
                  <input
                    id="l-asking"
                    className="input input-narrow"
                    type="number"
                    min={0}
                    value={draft.rental_price}
                    onChange={(e) => setDraft({ ...draft, rental_price: Number(e.target.value) })}
                  />
                </Field>

                <Field id="l-currency" label="Currency">
                  <input
                    id="l-currency"
                    className="input input-narrow"
                    maxLength={3}
                    value={draft.currency}
                    onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
                  />
                </Field>

                <div className="row">
                  <Field id="l-unit" label="Period">
                    <select
                      id="l-unit"
                      className="select"
                      value={draft.period_unit}
                      onChange={(e) => setDraft({ ...draft, period_unit: e.target.value as PeriodUnit })}
                    >
                      {PERIOD_UNIT.map((p) => (
                        <option key={p} value={p}>
                          {humanize(p)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field id="l-count" label="Periods per unit">
                    <input
                      id="l-count"
                      className="input input-narrow"
                      type="number"
                      min={1}
                      value={draft.period_count}
                      onChange={(e) => setDraft({ ...draft, period_count: Number(e.target.value) })}
                    />
                  </Field>
                  <Field id="l-min" label="Minimum">
                    <input
                      id="l-min"
                      className="input input-narrow"
                      type="number"
                      min={1}
                      value={draft.min_periods}
                      onChange={(e) => setDraft({ ...draft, min_periods: Number(e.target.value) })}
                    />
                  </Field>
                  <Field id="l-max" label="Maximum">
                    <input
                      id="l-max"
                      className="input input-narrow"
                      type="number"
                      min={1}
                      value={draft.max_periods}
                      onChange={(e) => setDraft({ ...draft, max_periods: Number(e.target.value) })}
                    />
                  </Field>
                </div>

                <fieldset className="perm-picker">
                  <legend>Permissions this listing grants</legend>
                  <p className="field-hint">
                    Anything you leave out is absent for the renter, not merely switched off.
                  </p>
                  {GRANTABLE_CAPABILITIES.map((c) => (
                    <label key={c} className="chip chip-check">
                      <input
                        type="checkbox"
                        checked={draft.granted_permissions.includes(c)}
                        onChange={() =>
                          setDraft({
                            ...draft,
                            granted_permissions: draft.granted_permissions.includes(c)
                              ? draft.granted_permissions.filter((x) => x !== c)
                              : [...draft.granted_permissions, c],
                          })
                        }
                      />
                      {c.replace(/_/g, ' ')}
                    </label>
                  ))}
                </fieldset>

                <div className="row">
                  <Field id="l-ctext" label="Included text credits">
                    <input
                      id="l-ctext"
                      className="input input-narrow"
                      type="number"
                      min={0}
                      value={draft.included_generation_credits.text}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          included_generation_credits: {
                            ...draft.included_generation_credits,
                            text: Number(e.target.value),
                          },
                        })
                      }
                    />
                  </Field>
                  <Field id="l-cimg" label="Included image credits">
                    <input
                      id="l-cimg"
                      className="input input-narrow"
                      type="number"
                      min={0}
                      value={draft.included_generation_credits.image}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          included_generation_credits: {
                            ...draft.included_generation_credits,
                            image: Number(e.target.value),
                          },
                        })
                      }
                    />
                  </Field>
                  <Field id="l-bearer" label="Who covers overage">
                    <select
                      id="l-bearer"
                      className="select"
                      value={draft.generation_overage_bearer}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          generation_overage_bearer: e.target.value as 'renter' | 'owner',
                        })
                      }
                    >
                      <option value="renter">{RENTER_LABEL}</option>
                      <option value="owner">{OWNER_LABEL}</option>
                    </select>
                  </Field>
                </div>

                <Field id="l-terms" label="Your terms" hint="Shown on the public profile before anyone requests.">
                  <textarea
                    id="l-terms"
                    className="textarea"
                    rows={5}
                    value={draft.terms}
                    onChange={(e) => setDraft({ ...draft, terms: e.target.value })}
                  />
                </Field>

                {save.problem ? <InlineError problem={save.problem} /> : null}
                <button type="submit" className="btn btn-primary" disabled={disabled || save.pending}>
                  {save.pending ? 'Saving…' : existing ? 'Save the listing' : 'Publish this listing'}
                </button>
                {save.data ? <LiveRegion>Listing saved.</LiveRegion> : null}
                {!existing ? (
                  <p className="muted">
                    Publishing starts the monthly listing subscription charged by the platform.
                  </p>
                ) : null}
              </fieldset>
            </form>
          </Card>
        )}
      </Gate>
    </div>
  );
}
